import { useState, useMemo, useEffect } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Input } from './ui/input';
import { MagnifyingGlass, CaretDown, Check } from '@phosphor-icons/react';

/**
 * Searchable cashier picker.
 *
 * Same pattern as SearchableCourseSelect — a popover with a pinned search
 * input above a scrollable list of cashier rows. Each row shows the
 * cashier's name plus an inline-muted mobile, so power users can pick by
 * either dimension.
 *
 * Props:
 *   • value           - selected agent_id (the cashier's 9-digit mobile tail)
 *   • onChange        - (mobile_tail) => void
 *   • cashiers        - array of { id, user_fullname, user_mobile, ... }
 *   • placeholder     - placeholder when no value (default: 'Select cashier')
 *   • disabled        - boolean
 *   • visibleCount    - number of rows before scrolling (default: 7)
 *   • testid          - data-testid on the trigger; sub-ids derived from it
 */
export default function SearchableCashierSelect({
  value,
  onChange,
  cashiers = [],
  placeholder = 'Select cashier',
  disabled = false,
  visibleCount = 7,
  testid,
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');

  useEffect(() => {
    if (!open) setQ('');
  }, [open]);

  const tail = (m) => String(m || '').replace(/\D/g, '');

  const selected = useMemo(
    () => cashiers.find((c) => tail(c.user_mobile) === String(value || '')) || null,
    [cashiers, value],
  );

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return cashiers;
    return cashiers.filter((c) => {
      const name = String(c.user_fullname || c.user_name || '').toLowerCase();
      const mob = tail(c.user_mobile);
      return name.includes(term) || mob.includes(term);
    });
  }, [cashiers, q]);

  const rowPx = 48;
  const maxH = `${rowPx * visibleCount}px`;

  const triggerLabel = selected
    ? (selected.user_fullname || selected.user_name || tail(selected.user_mobile))
    : cashiers.length === 0
      ? 'No cashiers available'
      : placeholder;

  return (
    <Popover open={open} onOpenChange={(v) => !disabled && setOpen(v)}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          data-testid={testid}
          title={selected ? (selected.user_fullname || '') : ''}
          className="flex h-11 w-full items-center justify-between gap-2 rounded-xl border border-[#E2E8E5] bg-transparent px-3 py-2 text-sm text-left disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-[#0A2540]/20"
        >
          <span
            className={`flex-1 min-w-0 truncate ${selected ? 'text-[#1C1917]' : 'text-[#A8A29E]'}`}
          >
            {triggerLabel}
            {selected && (
              <span className="ml-2 text-[11px] text-[#A8A29E]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                {tail(selected.user_mobile)}
              </span>
            )}
          </span>
          <CaretDown size={16} className="text-[#6C7A75] shrink-0" weight="bold" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0 rounded-xl border-[#E2E8E5] shadow-lg"
        align="start"
        sideOffset={6}
      >
        <div className="flex items-center gap-2 border-b border-[#E2E8E5] px-3 py-2">
          <MagnifyingGlass size={16} className="text-[#6C7A75] shrink-0" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name or mobile…"
            autoFocus
            data-testid={testid ? `${testid}-search` : 'cashier-search'}
            className="h-8 border-0 px-0 py-0 shadow-none focus-visible:ring-0 text-sm placeholder:text-[#A8A29E]"
          />
        </div>
        <div
          className="overflow-y-auto py-1"
          style={{ maxHeight: maxH }}
          data-testid={testid ? `${testid}-list` : 'cashier-list'}
        >
          {filtered.length === 0 ? (
            <div
              className="px-3 py-4 text-xs text-[#6C7A75] text-center"
              style={{ fontFamily: 'JetBrains Mono, monospace' }}
            >
              {cashiers.length === 0 ? 'No cashiers available.' : 'No matches.'}
            </div>
          ) : (
            filtered.map((c) => {
              const mob = tail(c.user_mobile);
              const isSelected = mob === String(value || '');
              const name = c.user_fullname || c.user_name || mob;
              const campus = c.campus_name || '';
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => {
                    onChange?.(mob);
                    setOpen(false);
                  }}
                  data-testid={testid ? `${testid}-option-${mob}` : `cashier-option-${mob}`}
                  title={name}
                  className={`flex w-full items-start justify-between gap-2 px-3 py-2 text-sm text-left hover:bg-[#F5F0E8] transition-colors ${
                    isSelected ? 'bg-[#FCFAF6]' : ''
                  }`}
                  style={{ minHeight: rowPx }}
                >
                  <span className="flex-1 min-w-0 leading-snug">
                    <span className="block text-[#1C1917] truncate">{name}</span>
                    <span
                      className="block text-[11px] text-[#A8A29E] truncate"
                      style={{ fontFamily: 'JetBrains Mono, monospace' }}
                    >
                      {mob}{campus ? ` · ${campus}` : ''}
                    </span>
                  </span>
                  {isSelected && (
                    <Check size={16} className="text-[#0A2540] shrink-0 mt-0.5" weight="bold" />
                  )}
                </button>
              );
            })
          )}
        </div>
        {cashiers.length > visibleCount && filtered.length > visibleCount && (
          <div
            className="border-t border-[#E2E8E5] px-3 py-1.5 text-[10px] uppercase tracking-widest text-[#A8A29E]"
            style={{ fontFamily: 'JetBrains Mono, monospace' }}
          >
            Scroll for more • {filtered.length} matches
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
